import { trpc } from "@/lib/trpc";
import { PermissionGuard } from "@/components/PermissionGuard";
import { useParams, useLocation } from "wouter";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, CheckCheck, Check, Clock, XCircle, Users, Send, Eye, Ban, AlertTriangle, ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import CampaignInsightsCard from "@/components/ai/CampaignInsightsCard";
import { useState } from "react";

const statusConfig: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  pending:   { label: "Pendente",   color: "bg-yellow-100 text-yellow-800",  icon: <Clock className="w-3 h-3" /> },
  sent:      { label: "Enviado",    color: "bg-blue-100 text-blue-800",      icon: <Check className="w-3 h-3" /> },
  delivered: { label: "Entregue",   color: "bg-green-100 text-green-800",    icon: <CheckCheck className="w-3 h-3" /> },
  read:      { label: "Lido",       color: "bg-emerald-100 text-emerald-800",icon: <Eye className="w-3 h-3" /> },
  failed:    { label: "Falhou",     color: "bg-red-100 text-red-800",        icon: <XCircle className="w-3 h-3" /> },
  aborted:   { label: "Abortado",   color: "bg-orange-100 text-orange-800",  icon: <Ban className="w-3 h-3" /> },
};

const PAGE_SIZE = 50;

// Detecta se o erro dominante das falhas é de bloqueio na Meta — nesse caso
// o operador precisa ir no Business Manager destravar pagamento/elegibilidade.
function isBillingBlock(errorMessage: string | null): boolean {
  if (!errorMessage) return false;
  const lower = errorMessage.toLowerCase();
  return (
    lower.includes("business eligibility") ||
    lower.includes("payment") ||
    lower.includes("waba is not eligible") ||
    lower.includes("account is locked") ||
    lower.includes("messaging limit")
  );
}

export default function CampaignReport() {
  const params = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const campaignId = Number(params.id);

  const [page, setPage] = useState(0);
  type StatusFilter = "all" | "pending" | "sent" | "delivered" | "read" | "failed" | "aborted";
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  const { data: campaign, refetch: refetchCampaign } = trpc.campaigns.get.useQuery({ id: campaignId });
  const { data: counts, refetch: refetchCounts, isFetching: isFetchingCounts } = trpc.campaignReport.counts.useQuery({ campaignId });
  const { data: failureBreakdown = [], refetch: refetchFailures } = trpc.campaignReport.failureBreakdown.useQuery({ campaignId, limit: 5 });
  const { data: messages = [], isLoading, refetch: refetchMessages, isFetching: isFetchingMessages } = trpc.campaignReport.messages.useQuery({
    campaignId,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    statusFilter: statusFilter === "all" ? undefined : statusFilter,
  });

  const isRefreshing = isFetchingCounts || isFetchingMessages;
  const refreshAll = () => {
    refetchCampaign();
    refetchCounts();
    refetchFailures();
    refetchMessages();
  };

  const stats = {
    total: counts?.total ?? 0,
    sent: (counts?.sent ?? 0) + (counts?.delivered ?? 0) + (counts?.read ?? 0),
    delivered: (counts?.delivered ?? 0) + (counts?.read ?? 0),
    read: counts?.read ?? 0,
    failed: counts?.failed ?? 0,
    aborted: counts?.aborted ?? 0,
  };

  const deliveryRate = stats.total > 0 ? Math.round((stats.delivered / stats.total) * 100) : 0;
  const readRate = stats.total > 0 ? Math.round((stats.read / stats.total) * 100) : 0;

  const dominantFailure = failureBreakdown[0]?.errorMessage ?? null;
  const billingBlocked = isBillingBlock(dominantFailure);

  const totalPages = statusFilter === "all"
    ? Math.ceil(stats.total / PAGE_SIZE)
    : Math.ceil((counts?.[statusFilter as keyof typeof counts] as number ?? 0) / PAGE_SIZE);

  return (
    <PermissionGuard permission="reports:view_whatsapp">
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate("/broadcast")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-foreground">
              Relatório: {campaign?.name ?? "Carregando..."}
            </h1>
            <p className="text-muted-foreground text-sm">Detalhamento de entrega por contato</p>
          </div>
          <Button variant="outline" size="sm" onClick={refreshAll} disabled={isRefreshing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? "animate-spin" : ""}`} />
            Atualizar
          </Button>
        </div>

        {/* Banner de bloqueio Meta — causa-raiz das 1854 falhas. Só aparece se o
            erro dominante das falhas for relacionado a billing/elegibilidade. */}
        {billingBlocked && (
          <div className="border border-red-200 bg-red-50 rounded-lg p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-900">
                Disparo bloqueado pela Meta
              </p>
              <p className="text-sm text-red-800 mt-1">
                Sua conta WhatsApp Business está com pendência de pagamento ou elegibilidade. Erro reportado:{" "}
                <span className="font-mono text-xs bg-red-100 px-1 py-0.5 rounded">{dominantFailure}</span>
              </p>
              <a
                href="https://business.facebook.com/billing_hub/payment_settings"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-2 text-sm font-medium text-red-700 underline hover:text-red-900"
              >
                Acessar Business Manager para regularizar →
              </a>
            </div>
          </div>
        )}

        {/* Breakdown de erros (top 5) — sempre que tiver falhas */}
        {failureBreakdown.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Top causas de falha</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-1.5">
                {failureBreakdown.map((row, i) => (
                  <li key={i} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground truncate flex-1 mr-2">{row.errorMessage ?? "(sem mensagem)"}</span>
                    <Badge variant="outline" className="font-mono">{row.count}</Badge>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Total</span>
              </div>
              <p className="text-2xl font-bold mt-1">{stats.total}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-2">
                <Send className="w-4 h-4 text-blue-500" />
                <span className="text-sm text-muted-foreground">Enviados</span>
              </div>
              <p className="text-2xl font-bold mt-1 text-blue-600">{stats.sent}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-2">
                <CheckCheck className="w-4 h-4 text-green-500" />
                <span className="text-sm text-muted-foreground">Entregues</span>
              </div>
              <p className="text-2xl font-bold mt-1 text-green-600">{stats.delivered}</p>
              <p className="text-xs text-muted-foreground">{deliveryRate}%</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-emerald-500" />
                <span className="text-sm text-muted-foreground">Lidos</span>
              </div>
              <p className="text-2xl font-bold mt-1 text-emerald-600">{stats.read}</p>
              <p className="text-xs text-muted-foreground">{readRate}%</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-2">
                <XCircle className="w-4 h-4 text-red-500" />
                <span className="text-sm text-muted-foreground">Falhas</span>
              </div>
              <p className="text-2xl font-bold mt-1 text-red-600">{stats.failed}</p>
            </CardContent>
          </Card>
          {stats.aborted > 0 && (
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-2">
                  <Ban className="w-4 h-4 text-orange-500" />
                  <span className="text-sm text-muted-foreground">Abortados</span>
                </div>
                <p className="text-2xl font-bold mt-1 text-orange-600">{stats.aborted}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* AI Insights */}
        {stats.total > 0 && (
          <CampaignInsightsCard
            campaignName={campaign?.name ?? "Campanha"}
            stats={{
              sent: stats.sent,
              delivered: stats.delivered,
              opened: stats.read,
              clicked: 0,
              bounced: stats.failed + stats.aborted,
              unsubscribed: 0,
            }}
          />
        )}

        {/* Tabela de mensagens */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <CardTitle>Detalhamento por Contato</CardTitle>
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v as StatusFilter); setPage(0); }}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Filtrar por status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os status</SelectItem>
                  <SelectItem value="sent">Enviados</SelectItem>
                  <SelectItem value="delivered">Entregues</SelectItem>
                  <SelectItem value="read">Lidos</SelectItem>
                  <SelectItem value="failed">Falhas</SelectItem>
                  <SelectItem value="aborted">Abortados</SelectItem>
                  <SelectItem value="pending">Pendentes</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">Carregando...</div>
            ) : messages.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Nenhuma mensagem encontrada para os filtros atuais.
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-muted-foreground">
                        <th className="text-left py-3 px-2">Contato</th>
                        <th className="text-left py-3 px-2">Empresa</th>
                        <th className="text-left py-3 px-2">Telefone</th>
                        <th className="text-left py-3 px-2">Status</th>
                        <th className="text-left py-3 px-2">Erro</th>
                        <th className="text-left py-3 px-2">Enviado em</th>
                        <th className="text-left py-3 px-2">Lido em</th>
                      </tr>
                    </thead>
                    <tbody>
                      {messages.map((msg) => {
                        const cfg = statusConfig[msg.status] ?? statusConfig.pending;
                        return (
                          <tr key={msg.id} className="border-b hover:bg-muted/30 transition-colors">
                            <td className="py-3 px-2 font-medium">{msg.contactName ?? "—"}</td>
                            <td className="py-3 px-2 text-muted-foreground">{msg.contactCompany ?? "—"}</td>
                            <td className="py-3 px-2 text-muted-foreground">{msg.contactPhone ?? "—"}</td>
                            <td className="py-3 px-2">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
                                {cfg.icon}
                                {cfg.label}
                              </span>
                            </td>
                            <td className="py-3 px-2 text-muted-foreground text-xs max-w-[240px] truncate" title={msg.errorMessage ?? undefined}>
                              {msg.errorMessage ?? "—"}
                            </td>
                            <td className="py-3 px-2 text-muted-foreground">
                              {msg.sentAt ? new Date(msg.sentAt).toLocaleString("pt-BR") : "—"}
                            </td>
                            <td className="py-3 px-2 text-muted-foreground">
                              {msg.readAt ? new Date(msg.readAt).toLocaleString("pt-BR") : "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Paginação */}
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Página {page + 1}{totalPages > 0 ? ` de ${totalPages}` : ""} — {messages.length} {messages.length === 1 ? "registro" : "registros"} nesta página
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                      <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
                    </Button>
                    <Button variant="outline" size="sm" disabled={totalPages > 0 && page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>
                      Próxima <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
    </PermissionGuard>
  );
}
